type RoleCarrier =
  | {
      primaryRole?: string;
      roles?: string[];
    }
  | null
  | undefined;

const ROLE_ALIAS: Record<string, string> = {
  operation_user: "operations_user",
  operations: "operations_user",
  // Space/hyphen variants are normalised to underscores by normalizeRole() before lookup,
  // so only the underscore-form keys are reachable here.
  relational_manager: "relational_manager",
  rm: "relational_manager",
  basic_user: "basic_user",
  bu: "basic_user",
};

function normalizeRole(role: string): string {
  const normalized = role
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return ROLE_ALIAS[normalized] || normalized;
}

function normalizedUserRoles(user: RoleCarrier): string[] {
  const collected = [
    ...(user?.primaryRole ? [user.primaryRole] : []),
    ...(user?.roles || []),
  ]
    .filter(
      (role): role is string =>
        typeof role === "string" && role.trim().length > 0,
    )
    .map((role) => normalizeRole(role.trim()));

  return Array.from(new Set(collected));
}

export function userHasRole(user: RoleCarrier, role: string): boolean {
  const wanted = normalizeRole(role);
  return normalizedUserRoles(user).includes(wanted);
}

export function isAdminUser(user: RoleCarrier): boolean {
  return userHasRole(user, "admin") || userHasRole(user, "super_admin");
}

export function isRelationalManager(user: RoleCarrier): boolean {
  return userHasRole(user, "relational_manager");
}

export function isBasicUser(user: RoleCarrier): boolean {
  return userHasRole(user, "basic_user");
}

export function canCreateTrainingMaterials(user: RoleCarrier): boolean {
  return (
    isAdminUser(user) ||
    userHasRole(user, "business_head") ||
    userHasRole(user, "operations_user")
  );
}

export function canManagePayouts(user: RoleCarrier): boolean {
  return isAdminUser(user) || userHasRole(user, "operations_user");
}